<template>
  <div class="content-link-page-container">
    <div :class="['content-link-text-content', { 'no-image': !hasImage }]">
      <div :class="['image-container', { 'no-image': !hasImage }]" class="">
        <transition name="appear">
          <img
            v-if="hasImage && !imageBroken"
            v-show="imageLoaded"
            :src="imageUrl"
            class="content-link-thumbnail"
            @error="handleLoadImageError"
            @load="handleLoaded"
          />
          <BrokenImage v-else-if="imageBroken" />
        </transition>
      </div>
      <div :class="['content-link-text-container', { 'no-image': !hasImage }]">
        <div class="text-container">
          <h3 class="title">{{ title }}</h3>
          <p class="description">{{ description }}</p>
        </div>

        <FriedFloater
          class="button-container"
          :show="tooltipOpen"
          placement="bottom-end"
          :main-axis-offset="0"
          :cross-axis-offset="8"
        >
          <div v-click-outside="closeTooltip" class="url-tooltip">
            <a :href="url" target="_blank" @click="closeTooltip">{{ url }}</a>
          </div>
          <template #reference>
            <ThemedButton
              class="open-link-btn"
              :theme="theme"
              data-external="content-link-page-open-link-button"
              @click="handleClick"
            >
              <span class="open-link-btn-text"> {{ buttonText }}</span>
            </ThemedButton>
          </template>
        </FriedFloater>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { ContentLinkBlock } from '@getaccept/lib-shared-new/src/types/content-link';
import type { EntityTheme } from '@getaccept/lib-shared-new/src/types/theme';
import ThemedButton from '@getaccept/lib-shared-new/src/components/ThemedButton.vue';
import { useI18n } from 'vue-i18n';
import { UrlHelper } from '@getaccept/lib-shared-new/src/helpers/url.helper';
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from 'vue';
import type { PropType } from 'vue';
import type { SigningTheme } from '@getaccept/lib-shared-new/src/signing-theme/types/theme';
import BrokenImage from './BrokenImage.vue';

export default defineComponent({
  components: {
    ThemedButton,
    BrokenImage,
  },
  props: {
    contentLinkBlock: {
      type: Object as PropType<ContentLinkBlock>,
      default: () => ({}),
    },
    theme: {
      type: Object as PropType<EntityTheme | SigningTheme>,
      default: null,
    },
    documentEdit: {
      type: Boolean,
    },
  },
  emits: ['click'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const imageBroken = ref(false);
    const imageLoaded = ref(false);
    const tooltipOpen = ref(false);

    const hasImage = computed(() => !!imageUrl.value);

    const buttonText = computed(() => props.contentLinkBlock?.buttonText || t('open-link'));

    const title = computed(() => props.contentLinkBlock?.title || '');

    const url = computed(() => props.contentLinkBlock?.url || '');

    const description = computed(() => props.contentLinkBlock?.description || '');

    const imageUrl = computed((): string => props.contentLinkBlock?.thumbUrl || '');

    const handleClick = () => {
      if (props.documentEdit) {
        showTooltip();
      } else {
        openUrl(url.value);
      }
    };

    const handleKeydown = event => {
      if (event.key === 'Escape') {
        closeTooltip();
      }
    };

    const showTooltip = () => {
      tooltipOpen.value = true;
    };

    const closeTooltip = () => {
      tooltipOpen.value = false;
    };

    const openUrl = (contentLinkUrl: string) => {
      const url = UrlHelper.getHttpsUrl(contentLinkUrl);
      window.open(url, '_blank');
      emit('click', props.contentLinkBlock.id);
      closeTooltip();
    };

    const handleLoadImageError = () => {
      imageBroken.value = true;
    };

    const handleLoaded = () => {
      imageLoaded.value = true;
    };

    onMounted(() => {
      if (props.documentEdit) {
        document.addEventListener('keydown', handleKeydown);
      }
    });

    onBeforeUnmount(() => {
      document.removeEventListener('keydown', handleKeydown);
    });

    return {
      imageBroken,
      imageLoaded,
      tooltipOpen,
      hasImage,
      imageUrl,
      buttonText,
      title,
      url,
      description,
      handleClick,
      closeTooltip,
      handleLoadImageError,
      handleLoaded,
      showTooltip,
      handleKeydown,
      openUrl,
    };
  },
});
</script>

<style lang="scss" scoped>
$open-link-btn-width: 200px;
$link-image-max-height: 195px;
$link-image-width: 260px;
$text-container-width: 66%;

.content-link-page-container {
  display: flex;
  background: var(--white);
  padding: var(--spacing-150);
  font-size: 14px;
  flex-direction: column;

  @media screen and (min-width: $xs) {
    border-radius: var(--radius-medium);
  }

  .content-link-text-content {
    display: flex;
    flex-direction: row;
    width: 100%;

    &.no-image {
      width: 100%;
    }

    @media only screen and (max-width: $sm) {
      flex-direction: column;
    }
  }

  @media only screen and (max-width: $sm) {
    flex-direction: column;
    padding: var(--spacing-100);
  }

  .image-container {
    width: $link-image-width;

    @supports not (-ms-high-contrast: none) {
      display: flex;
      flex: $link-image-width;
    }

    @media only screen and (max-width: $sm) {
      flex: auto;
      padding-top: var(--spacing-150);
      width: auto;
    }

    &.no-image {
      display: none;
    }

    .content-link-thumbnail {
      align-self: center;
      max-width: 100%;
      max-height: $link-image-max-height;
      object-fit: scale-down;
      border-radius: var(--radius-medium);

      @media only screen and (max-width: $sm) {
        margin-top: var(--spacing-100);
        margin-left: auto;
        margin-right: auto;
      }
    }
  }

  .content-link-text-container {
    padding-top: 2.4rem;
    display: flex;
    justify-content: space-between;
    flex-flow: column wrap;
    flex: 2 2 $text-container-width;
    width: $text-container-width;
    padding-left: var(--spacing-150);

    @media only screen and (max-width: $sm) {
      padding-top: 0.8rem;
      flex: auto;
      padding-left: 0;
      width: auto;
    }

    &.no-image {
      width: 100%;
      padding-left: 0;
    }

    .text-container {
      width: 100%;

      .title {
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: normal;
        margin: 0;
      }

      .description {
        line-height: 1.6;
        margin: var(--spacing-50) 0 var(--spacing-100) 0;
        white-space: pre-line;
      }
    }

    .button-container {
      align-self: flex-end;

      @media only screen and (max-width: $xs) {
        width: 100%;
      }
    }

    .open-link-btn {
      width: fit-content;
      align-self: flex-end;
      border: none;
      min-width: $open-link-btn-width;

      @media only screen and (max-width: $xs) {
        width: 100%;
        margin-top: var(--spacing-100);
      }

      .open-link-btn-text {
        margin: auto;
        font-size: var(--paragraph-medium-font-size);
        font-weight: 400;
        overflow-wrap: anywhere;
      }
    }
  }

  .url-tooltip {
    background: white;
    box-shadow: var(--shadow-2);
    border-radius: var(--radius-medium);
    height: 36px;
    padding: 0 var(--spacing-100);
    display: flex;
    align-items: center;
    z-index: $z-tooltip;
    max-width: 352px;

    a {
      overflow: hidden;
      display: block;
      text-overflow: ellipsis;
      text-decoration: none;
      line-height: initial;
      color: var(--blue-45);
      font-weight: 600;

      &:hover {
        text-decoration: underline;
      }
    }

    a[data-focus-visible-added] {
      outline-color: -webkit-focus-ring-color auto 1px;
    }
  }
}
</style>
